import React, { ComponentPropsWithoutRef, useMemo } from 'react';
import classnames from 'classnames';
import CheckCircleIcon from '../../assets/icons/CheckCircleIcon';
import CookingIcon from '../../assets/icons/CookingIcon';
import BoxIcon from '../../assets/icons/BoxIcon';
import { Order } from '../../services/exports/Interfaces';
import { DeliveryProvider, ORDER_FULFILLMENT_STATUSES, ORDER_METHODS } from '../../services/exports/Constants';
import { useSelector } from 'react-redux';
import { selectOngoingOrder } from '../../store/slices/OrderSlice';
import CloseIcon from '../../assets/icons/actions/CloseIcon';
import { useTranslation } from 'react-i18next';
import moment from 'moment/moment';
import useAppMethods from '../../hooks/utility/useAppMethods';
import ClockIcon from '../../assets/icons/order-methods/ClockIcon';
import collect from 'collect.js';
import DeliveryIcon from '../../assets/icons/order-methods/DeliveryIcon';
import PickupIcon from '../../assets/icons/order-methods/PickupIcon';

type OrderTrackerProps = ComponentPropsWithoutRef<'section'> & { order?: Order };

const OrderTracker = (props: OrderTrackerProps) => {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Order:OrderTracker' });
  const defaultOrder = useSelector(selectOngoingOrder);
  const ongoingOrder = props?.order ?? defaultOrder;
  const isRejected = ongoingOrder?.fulfillment?.status === ORDER_FULFILLMENT_STATUSES.REJECTED;
  const isPicked = ongoingOrder?.fulfillment?.status === ORDER_FULFILLMENT_STATUSES.PICKED;

  const isChecked = (status: ORDER_FULFILLMENT_STATUSES) => {
    if (isPicked) {
      return true;
    }

    return ongoingOrder?.fulfillment?.events.some((event) => event.status === status);
  };

  const isReadyChecked =
    isChecked(ORDER_FULFILLMENT_STATUSES.READY) || isChecked(ORDER_FULFILLMENT_STATUSES.ON_DELIVERY);
  const isPreparingChecked = isChecked(ORDER_FULFILLMENT_STATUSES.PREPARING);
  const isAcceptedChecked = isChecked(ORDER_FULFILLMENT_STATUSES.ACCEPTED);
  const readyTitle =
    ongoingOrder?.method === ORDER_METHODS.DELIVERY
      ? ORDER_FULFILLMENT_STATUSES.ON_DELIVERY
      : ORDER_FULFILLMENT_STATUSES.READY;

  const steps = useMemo(() => {
    const rawSteps = [
      {
        status: ORDER_FULFILLMENT_STATUSES.CREATED,
        show: true,
        checked: true,
        icon: BoxIcon,
        title: ORDER_FULFILLMENT_STATUSES.CREATED,
      },
      {
        show: !isRejected,
        status: ORDER_FULFILLMENT_STATUSES.ACCEPTED,
        checked: isAcceptedChecked,
        icon: CheckCircleIcon,
        title: ORDER_FULFILLMENT_STATUSES.ACCEPTED,
      },
      {
        show: isRejected,
        status: ORDER_FULFILLMENT_STATUSES.REJECTED,
        checked: true,
        icon: CloseIcon,
        title: ORDER_FULFILLMENT_STATUSES.REJECTED,
        danger: true,
      },
      {
        status: ORDER_FULFILLMENT_STATUSES.PREPARING,
        show: true,
        checked: isPreparingChecked,
        icon: CookingIcon,
        title: ORDER_FULFILLMENT_STATUSES.PREPARING,
      },
      {
        status: ORDER_FULFILLMENT_STATUSES.READY,
        show: true,
        checked: isReadyChecked,
        icon: ongoingOrder?.method === ORDER_METHODS.DELIVERY ? DeliveryIcon : PickupIcon,
        title: readyTitle,
        final: isReadyChecked,
      },
    ].filter((step) => step && step.show);

    let lastCheckedIndex = -1;
    for (let i = 0; i < rawSteps.length; i++) {
      if (rawSteps[i].checked) {
        lastCheckedIndex = i;
      }
    }

    return rawSteps.map((step, index) => ({
      ...step,
      checked: index <= lastCheckedIndex,
    }));
  }, [
    ongoingOrder?.fulfillment?.status,
    isRejected,
    isAcceptedChecked,
    isPreparingChecked,
    isReadyChecked,
    readyTitle,
  ]);

  const activeStep = useMemo(
    () =>
      collect(steps)
        .filter((step) => step.checked)
        .last(),
    [steps],
  );

  const pendingStep = useMemo(() => collect(steps).first((step) => !step.checked) ?? null, [steps]);

  return (
    <section
      className={classnames('space-y-md', props.className)}
      style={
        {
          '--border': 'hsl(240 6% 90%)',
          '--card': 'hsl(0 0% 100%)',
          '--muted': 'hsl(210 40% 96.1%)',
          '--muted-foreground': 'hsl(215 20% 65%)',
          '--success': 'hsl(142 76% 36%)',
        } as React.CSSProperties
      }
    >
      <div className="flex flex-col gap-2">
        <h6 className="font-normal text-sm text-center">
          {t('labels.order_number', {
            number: ongoingOrder?.fulfillment?.number,
          })}
        </h6>
        {!isRejected && <OrderTrackerTimeBar order={ongoingOrder} />}
      </div>

      <div className="flex flex-col w-full relative">
        <div className="flex w-full absolute">
          {steps.slice(0, -1).map((step) => {
            return (
              <div key={step.status} className="flex-1 flex h-fit mt-[14px]">
                <ProgressBar
                  checked={step.checked}
                  active={activeStep?.status === step.status}
                  isRejected={isRejected}
                />
              </div>
            );
          })}
        </div>
        <div className="flex w-full justify-between overflow-visible">
          {steps.map((step, index) => (
            <OrderTrackerItem
              key={step.status}
              checked={step.checked}
              pending={pendingStep?.status === step.status}
              active={activeStep?.status === step.status}
              danger={step?.danger}
              icon={step.icon}
              title={step.title}
              final={step.final}
              isRejected={isRejected}
              isFirst={index === 0}
              isLast={index === steps.length - 1}
            />
          ))}
        </div>
      </div>
      <div className="mt-sm flex flex-col gap-2">
        <h6
          className={classnames('text-sm', {
            'text-danger': isRejected,
          })}
        >
          {t('status.actual')}: {t(activeStep?.title)}
        </h6>
        {!isRejected && pendingStep && (
          <p className="text-sm text-brand-text-secondary">
            {t('status.pending')}: {t(pendingStep?.title)}
          </p>
        )}
      </div>
    </section>
  );
};

const OrderTrackerTimeBar = ({ order }: OrderTrackerProps) => {
  const { formatTime } = useAppMethods();
  const { t, i18n } = useTranslation(undefined, { keyPrefix: 'Shared:Calendar' });
  const { t: tOrderTracker } = useTranslation(null, { keyPrefix: 'Components:Order:OrderTracker' });
  const timeInterval = useMemo(() => {
    if (order?.fulfillment?.status === ORDER_FULFILLMENT_STATUSES.REJECTED) {
      return;
    }

    if (!order?.fulfillment?.estimated_pickup_time) {
      return;
    }

    const estimatedPickupTime = moment(order?.fulfillment?.estimated_pickup_time);
    const min = formatTime(estimatedPickupTime);
    const max = formatTime(estimatedPickupTime.add(10, 'minutes'));
    const date = estimatedPickupTime.isSame(moment(), 'day') ? t('today') : estimatedPickupTime.format('dd DD.MM');

    return `${min} - ${max}, ${date}`;
  }, [order?.fulfillment?.estimated_pickup_time, order?.fulfillment?.status, i18n.language]);

  const renderPickupTimeChangedLabel = useMemo(() => {
    if (
      !order?.fulfillment?.initial_confirmation_time ||
      !order?.fulfillment?.estimated_pickup_time ||
      (order?.method === ORDER_METHODS.DELIVERY && order?.delivery_provider !== DeliveryProvider.Standalone) ||
      order?.fulfillment?.initial_confirmation_time === order?.fulfillment?.estimated_pickup_time
    ) {
      return null;
    }

    return (
      <div className="w-fit p-sm bg-notice rounded-lg">
        <p className="text-sm">
          {tOrderTracker('warning_label.pickup_time_changed', {
            company: order?.company?.name,
            initialTime: formatTime(order?.fulfillment?.initial_confirmation_time),
            currentTime: formatTime(order?.fulfillment?.estimated_pickup_time),
          })}
        </p>
      </div>
    );
  }, [order?.fulfillment?.initial_confirmation_time, order?.fulfillment?.estimated_pickup_time]);

  return (
    <div className="flex flex-col gap-3 w-full items-center">
      <div className="flex items-center justify-center gap-2">
        <ClockIcon className="!size-4" color="black" />
        <p>{timeInterval}</p>
      </div>
      {renderPickupTimeChangedLabel}
    </div>
  );
};

type ProgressBarProps = {
  checked?: boolean;
  active?: boolean;
  isRejected?: boolean;
};

const ProgressBar = (props: ProgressBarProps) => {
  const { checked, active, isRejected } = props;
  if (!checked || (active && isRejected)) {
    return <div className="bg-[var(--border)] h-[6px] w-full"></div>;
  }
  if (isRejected) {
    return <div className="bg-danger h-[6px] w-full"></div>;
  }
  if (!active) {
    return <div className="bg-green-500 h-[6px] w-full"></div>;
  }

  return (
    <div className="progress-container">
      <div className="progress-fill"></div>
    </div>
  );
};

type OrderTrackerItemProps = {
  checked: boolean;
  pending: boolean;
  active: boolean;
  icon: React.ComponentType<{ className: string; color?: string }>;
  title: string;
  danger?: boolean;
  isRejected?: boolean;
  final?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
};

const OrderTrackerItem = (props: OrderTrackerItemProps) => {
  const { isFirst, isLast, checked, active, danger, icon, isRejected } = props;

  return (
    <div
      className={classnames('flex flex-col gap-3 shrink-0 relative', {
        '!items-start': isFirst,
        '!items-end': isLast,
      })}
    >
      <div
        className={classnames(
          'relative z-10 size-9 rounded-full flex items-center justify-center transition-all duration-500 shrink-0',
          {
            'bg-[var(--card)]': !checked && !isRejected,
            shadow: checked,
            'bg-green-500 text-white': checked && !isRejected,
            'bg-[var(--muted)] text-[var(--muted-foreground)]': !checked,
            'text-green-500': checked && !isRejected,
            'bg-danger text-white': checked && isRejected,
          },
        )}
      >
        {active && (
          <span
            className={classnames('absolute inset-0 rounded-full ring-[4px] ring-green-500 opacity-20 animate-pulse', {
              '!ring-danger': danger,
            })}
          />
        )}
        {React.createElement(icon, {
          className: iconClassName,
          color: checked ? 'white' : '#94A3B8',
        })}
      </div>
    </div>
  );
};

const iconClassName = 'transition-colors duration-500 size-4';

export default OrderTracker;
